/**
 * Logger Service
 * 
 * This service provides secure logging functionality that is environment-aware:
 * - In development: Full logs are displayed in the console
 * - In production: Sensitive information is redacted and logs are only shown when needed
 * 
 * This prevents sensitive information exposure in production environments.
 */

// Environment detection
const isProduction = process.env.NODE_ENV === 'production';

// Sensitive data patterns to redact in production
const SENSITIVE_PATTERNS = [
  /password/i,
  /token/i,
  /auth/i,
  /key/i,
  /secret/i,
  /credential/i,
  /session/i,
  /email/i,
  /phone/i,
  /address/i,
  /user_id/i,
  /userId/i,
  /profile/i
];

/**
 * Redacts sensitive information from logs in production
 */
const redactSensitiveInfo = (data: any): any => {
  if (!isProduction) return data;
  if (!data) return data;
  
  // Handle strings
  if (typeof data === 'string') {
    let redacted = data;
    SENSITIVE_PATTERNS.forEach(pattern => {
      redacted = redacted.replace(new RegExp(`(${pattern.source}[^\\s:]*)[\\s:]+([^\\s]*)`, 'gi'), '$1: [REDACTED]');
    });
    return redacted;
  }
  
  // Handle objects recursively
  if (typeof data === 'object' && data !== null) {
    if (Array.isArray(data)) {
      return data.map(item => redactSensitiveInfo(item));
    }
    
    const redactedObj: Record<string, any> = {};
    Object.keys(data).forEach(key => {
      if (SENSITIVE_PATTERNS.some(pattern => pattern.test(key))) {
        redactedObj[key] = '[REDACTED]';
      } else {
        redactedObj[key] = redactSensitiveInfo(data[key]);
      }
    });
    return redactedObj;
  }
  
  return data;
};

/**
 * Creates a safe message for logging
 */
const createSafeMessage = (message: string, ...args: any[]): any[] => {
  const safeMessage = isProduction ? redactSensitiveInfo(message) : message;
  const safeArgs = isProduction ? args.map(arg => redactSensitiveInfo(arg)) : args;
  return [safeMessage, ...safeArgs];
};

const logger = {
  /**
   * Info level logging - only displayed in development or if explicitly enabled
   */
  info(message: string, ...args: any[]): void {
    if (!isProduction) {
      console.log(...createSafeMessage(message, ...args));
    }
  },
  
  /**
   * Debug level logging - only in development
   */
  debug(message: string, ...args: any[]): void {
    if (!isProduction) {
      console.log(`[DEBUG] ${message}`, ...args);
    }
  },
  
  /**
   * Warning level logging - displayed in both environments but sanitized in production
   */
  warn(message: string, ...args: any[]): void {
    if (isProduction) {
      console.warn(...createSafeMessage(`[WARNING] ${message}`, ...args));
    } else {
      console.warn(`[WARNING] ${message}`, ...args);
    }
  },
  
  /**
   * Error level logging - displayed in both environments but sanitized in production
   */
  error(message: string, error?: any, context?: Record<string, any>): void {
    if (isProduction) {
      // In production, we sanitize the error and limit what's logged
      const safeError = error ? {
        name: error.name || 'Error',
        message: redactSensitiveInfo(error.message || 'Unknown error'),
        code: error.code || 'UNKNOWN'
      } : null;
      
      const safeContext = redactSensitiveInfo(context || {});
      console.error(`[ERROR] ${redactSensitiveInfo(message)}`, safeError, safeContext);
    } else {
      // In development, we log full details
      console.error(`[ERROR] ${message}`, error, context);
    }
  },
  
  /**
   * Security event logging - always logged but heavily sanitized in production
   */
  security(message: string, details?: Record<string, any>): void {
    const securityEvent = {
      timestamp: new Date().toISOString(),
      message: redactSensitiveInfo(message),
      details: isProduction ? redactSensitiveInfo(details || {}) : details
    };
    
    if (isProduction) {
      // In production, we might send this to a security logging system
      console.warn('[SECURITY EVENT]', securityEvent);
    } else {
      console.warn('[SECURITY EVENT]', securityEvent);
    }
  }
};

export default logger; 